const CLOUDINARY_CLOUD_NAME = 'doqawy1zm';
const CLOUDINARY_UPLOAD_PRESET = 'cantieri_app';

function isImageFile(file) {
  return !!file && typeof file.type === 'string' && file.type.startsWith('image/');
}

function isFirmaFolder(cartella = '') {
  const nome = String(cartella).toLowerCase();
  return nome === 'firme' || nome === 'firme_dipendenti';
}

function renameWithJpegExtension(filename = 'image.jpg') {
  const clean = String(filename).trim() || 'image';
  const base = clean.replace(/\.[^/.]+$/, '');
  return `${base}.jpg`;
}


// Rimuove la versione dall'URL Cloudinary.
// Esempio:
// /image/upload/v1771497613/bolle/foto.jpg
// diventa:
// /image/upload/bolle/foto.jpg
function stripVersionFromUrl(url) {
  if (!url || typeof url !== 'string') {
    return url;
  }

  return url.replace(/\/v\d+\//, '/');
}



// Comprime un'immagine prima dell'upload.
// NON comprime le firme: questo viene deciso in uploadImmagine().
function comprimiImmagine(file, qualita = 0.7) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('COMPRESS_IMAGE_FILE_ASSENTE'));
      return;
    }

    if (!isImageFile(file)) {
      resolve(file);
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const MAX_DIM = 1920;
        let { width, height } = img;

        if (width > MAX_DIM || height > MAX_DIM) {
          if (width > height) {
            height = Math.round((height * MAX_DIM) / width);
            width = MAX_DIM;
          } else {
            width = Math.round((width * MAX_DIM) / height);
            height = MAX_DIM;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(url);
          reject(new Error('COMPRESS_IMAGE_CANVAS_CONTEXT_ERROR'));
          return;
        }

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);

            if (!blob) {
              reject(new Error('COMPRESS_IMAGE_BLOB_ERROR'));
              return;
            }

            const fileCompresso = new File([blob], renameWithJpegExtension(file.name), {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });

            resolve(fileCompresso);
          },
          'image/jpeg',
          qualita
        );
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('COMPRESS_IMAGE_DECODE_ERROR'));
    };

    img.src = url;
  });
}

// Upload su Cloudinary con compressione automatica
// Ritorna { imageUrl, publicId } oppure lancia un errore
export async function uploadImmagine(file, cartella = 'cantieri') {
  if (!file) {
    throw new Error('UPLOAD_FILE_ASSENTE');
  }

  const isFirma = isFirmaFolder(cartella);
  const fileDaInviare = isFirma ? file : await comprimiImmagine(file);

  const formData = new FormData();
  formData.append('file', fileDaInviare);
  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
  formData.append('folder', cartella);

  let response;

  try {
    response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );
  } catch (networkErr) {
    const err = new Error(
      `CLOUDINARY_NETWORK_ERROR | file=${fileDaInviare?.name || 'n/d'} | size=${fileDaInviare?.size || 0} | type=${fileDaInviare?.type || 'n/d'} | msg=${networkErr.message || 'Load failed'}`
    );
    err.code = 'CLOUDINARY_NETWORK_ERROR';
    err.originalMessage = networkErr.message || 'Load failed';
    err.fileName = fileDaInviare?.name || null;
    err.fileSize = fileDaInviare?.size || null;
    err.fileType = fileDaInviare?.type || null;
    err.folder = cartella;
    throw err;
  }

  if (!response.ok) {
    let errorText = '';
    let errorJson = null;
    const xCldError = response.headers.get('x-cld-error');

    try {
      errorJson = await response.clone().json();
    } catch (_) {}

    if (!errorJson) {
      try {
        errorText = await response.text();
      } catch (_) {}
    }

    const cloudinaryMessage =
      errorJson?.error?.message ||
      xCldError ||
      errorText ||
      response.statusText ||
      'Errore upload Cloudinary';

    const err = new Error(
      `CLOUDINARY_HTTP_ERROR | status=${response.status} | xCldError=${xCldError || 'n/d'} | file=${fileDaInviare?.name || 'n/d'} | size=${fileDaInviare?.size || 0} | type=${fileDaInviare?.type || 'n/d'} | msg=${cloudinaryMessage}`
    );
    err.code = 'CLOUDINARY_HTTP_ERROR';
    err.httpStatus = response.status;
    err.httpStatusText = response.statusText;
    err.xCldError = xCldError || null;
    err.responseBody = errorJson || errorText || null;
    err.fileName = fileDaInviare?.name || null;
    err.fileSize = fileDaInviare?.size || null;
    err.fileType = fileDaInviare?.type || null;
    err.folder = cartella;
    throw err;
  }

  let data;
  try {
    data = await response.json();
  } catch (_) {
    const err = new Error('CLOUDINARY_RESPONSE_INVALIDA');
    err.code = 'CLOUDINARY_RESPONSE_INVALIDA';
    err.fileName = fileDaInviare?.name || null;
    err.fileSize = fileDaInviare?.size || null;
    err.fileType = fileDaInviare?.type || null;
    err.folder = cartella;
    throw err;
  }

  if (!data?.secure_url || !data?.public_id) {
    const err = new Error('CLOUDINARY_RESPONSE_INCOMPLETA');
    err.code = 'CLOUDINARY_RESPONSE_INCOMPLETA';
    err.responseBody = data;
    err.fileName = fileDaInviare?.name || null;
    err.fileSize = fileDaInviare?.size || null;
    err.fileType = fileDaInviare?.type || null;
    err.folder = cartella;
    throw err;
  }

return {
  imageUrl: stripVersionFromUrl(data.secure_url),
  publicId: data.public_id,
};
}

// Sovrascrive l'immagine su Cloudinary con un placeholder 1x1 pixel trasparente
// usando lo stesso publicId, così l'immagine originale non è più accessibile.
export async function deleteImmagine(publicId) {
  const response = await fetch('https://api.shhhup.me/delete-image', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ publicId }),
  });

  if (!response.ok) {
    let errJson = null;

    try {
      errJson = await response.json();
    } catch (_) {}

    throw new Error(errJson?.error || 'Errore cancellazione Cloudinary');
  }

  const data = await response.json();

  if (data.result?.result !== 'ok' && data.result?.result !== 'not found') {
    throw new Error('Cloudinary: errore durante la cancellazione');
  }

  return true;
}